import type {
    PPALensConfigInput,
    PPASaveScenarioViewRequest,
    PPAScenarioColumnVisibilityInput,
} from 'types/ppaViews';

import { LENS_CATALOG_SECTION_KEYS } from './scenarioLensCatalog.types';
import type { LensCatalogData, LensCatalogItem } from './scenarioLensCatalog.types';
import type { ScenarioViewLens } from './scenarioLens.types';
import { sortLensesByDisplayOrder } from './scenarioLensOrder.helpers';

export type DeltaLensOrderInput = {
    lens_id: number;
    is_selected: boolean;
    display_order: number;
    is_default_lens?: boolean;
};

export type DeltaLensViewPayload = {
    column_visibility: PPAScenarioColumnVisibilityInput[];
    lensOrderPayload: DeltaLensOrderInput[];
};

const normalizeSectionKey = (sectionKey: string) => sectionKey.trim().toLowerCase();

const resolveColumnVisibilityItemType = (item: LensCatalogItem, sectionKey: string): string => {
    if (
        item.attribute_nature === 'SYSTEM_COLUMN' ||
        normalizeSectionKey(sectionKey) === LENS_CATALOG_SECTION_KEYS.SYSTEM_COLUMNS
    ) {
        return 'SYSTEM_COLUMN';
    }

    return 'ATTRIBUTE';
};

export const getCatalogSelectionSignature = (catalog: LensCatalogData): string =>
    catalog.sections
        .flatMap((section) =>
            section.groups.flatMap((group) =>
                group.items.map(
                    (item) => `${item.attribute_key}:${item.is_selected ? 1 : 0}`,
                ),
            ),
        )
        .sort()
        .join('|');

export const flattenCatalogToColumnVisibility = (
    lensId: number,
    catalog: LensCatalogData,
): PPAScenarioColumnVisibilityInput[] =>
    catalog.sections.flatMap((section) =>
        section.groups.flatMap((group) =>
            group.items.map((item) => ({
                lens_id: lensId,
                attribute_key: item.attribute_key,
                item_type: resolveColumnVisibilityItemType(item, section.section_key),
                is_display_enabled: item.is_selected,
            })),
        ),
    );

export const areLensMappingsEqual = (
    baselineLenses: ScenarioViewLens[],
    nextLenses: ScenarioViewLens[],
): boolean => {
    const orderedBaseline = sortLensesByDisplayOrder(baselineLenses);
    const orderedNext = sortLensesByDisplayOrder(nextLenses);

    if (orderedBaseline.length !== orderedNext.length) {
        return false;
    }

    return orderedBaseline.every((lens, index) => {
        const other = orderedNext[index];
        return (
            lens.lens_id === other.lens_id &&
            lens.is_selected === other.is_selected &&
            lens.display_order === other.display_order
        );
    });
};

export const isCatalogSelectionDirty = (
    lensId: number,
    catalog: LensCatalogData,
    resolveBaselineCatalog: (lensId: number) => LensCatalogData | null,
): boolean => {
    const baseline = resolveBaselineCatalog(lensId);

    if (!baseline) {
        return true;
    }

    return getCatalogSelectionSignature(baseline) !== getCatalogSelectionSignature(catalog);
};

export const computeEditedCatalogLensIds = (params: {
    catalogDraftsByLensId: Record<number, LensCatalogData>;
    catalogEditingByLensId?: Record<number, LensCatalogData>;
    resolveBaselineCatalog: (lensId: number) => LensCatalogData | null;
}): number[] => {
    const candidateLensIds = new Set<number>();

    for (const key of Object.keys(params.catalogDraftsByLensId)) {
        candidateLensIds.add(Number(key));
    }

    for (const key of Object.keys(params.catalogEditingByLensId ?? {})) {
        candidateLensIds.add(Number(key));
    }

    return [...candidateLensIds].filter((lensId) => {
        const catalog =
            params.catalogEditingByLensId?.[lensId] ?? params.catalogDraftsByLensId[lensId];

        return catalog
            ? isCatalogSelectionDirty(lensId, catalog, params.resolveBaselineCatalog)
            : false;
    });
};

export const computeSelectionDirtyLensIds = (
    catalogDraftsByLensId: Record<number, LensCatalogData>,
    resolveBaselineCatalog: (lensId: number) => LensCatalogData | null,
): number[] =>
    computeEditedCatalogLensIds({
        catalogDraftsByLensId,
        resolveBaselineCatalog,
    });

export const buildDeltaLensViewPayload = (params: {
    selectionDirtyLensIds: number[];
    lensMappingDirty: boolean;
    catalogDraftsByLensId: Record<number, LensCatalogData>;
    resolveCatalogForLens: (lensId: number) => LensCatalogData | null;
    syncedLenses: ScenarioViewLens[];
    includeDefaultLens?: boolean;
}): DeltaLensViewPayload => {
    const column_visibility = params.selectionDirtyLensIds.flatMap((lensId) => {
        const catalog =
            params.catalogDraftsByLensId[lensId] ?? params.resolveCatalogForLens(lensId);

        if (!catalog) {
            return [];
        }

        return flattenCatalogToColumnVisibility(lensId, catalog);
    });

    const lensOrderPayload: DeltaLensOrderInput[] = params.lensMappingDirty
        ? sortLensesByDisplayOrder(params.syncedLenses).map((lens) => ({
              lens_id: lens.lens_id,
              is_selected: lens.is_selected,
              display_order: lens.display_order,
              ...(params.includeDefaultLens ? { is_default_lens: false } : {}),
          }))
        : [];

    return { column_visibility, lensOrderPayload };
};

export const mapLensOrderToLensConfig = (
    lensOrderPayload: DeltaLensOrderInput[],
): PPALensConfigInput[] =>
    lensOrderPayload.map((lens) => ({
        lens_id: lens.lens_id,
        is_selected: lens.is_selected,
        display_order: lens.display_order,
        is_default_lens: lens.is_default_lens ?? false,
    }));

export const buildColumnVisibilityFromLenses = (params: {
    lenses: ScenarioViewLens[];
    resolveCatalogForLens: (lensId: number) => LensCatalogData | null;
    fallbackColumnVisibility?: PPAScenarioColumnVisibilityInput[];
}): PPAScenarioColumnVisibilityInput[] => {
    const orderedLenses = sortLensesByDisplayOrder(params.lenses);
    const fallbackByLensId = new Map<number, PPAScenarioColumnVisibilityInput[]>();

    for (const row of params.fallbackColumnVisibility ?? []) {
        const existing = fallbackByLensId.get(row.lens_id) ?? [];
        existing.push(row);
        fallbackByLensId.set(row.lens_id, existing);
    }

    return orderedLenses.flatMap((lens) => {
        const catalog = params.resolveCatalogForLens(lens.lens_id);
        if (catalog) {
            return flattenCatalogToColumnVisibility(lens.lens_id, catalog);
        }

        return fallbackByLensId.get(lens.lens_id) ?? [];
    });
};

export const buildFullScenarioViewPayload = (params: {
    scenarioId: number;
    userId?: string;
    syncedLenses: ScenarioViewLens[];
    resolveCatalogForLens: (lensId: number) => LensCatalogData | null;
    fallbackColumnVisibility?: PPAScenarioColumnVisibilityInput[];
}): PPASaveScenarioViewRequest => {
    const orderedLenses = sortLensesByDisplayOrder(params.syncedLenses);
    const column_visibility = buildColumnVisibilityFromLenses({
        lenses: orderedLenses,
        resolveCatalogForLens: params.resolveCatalogForLens,
        fallbackColumnVisibility: params.fallbackColumnVisibility,
    });

    return {
        scenario_id: params.scenarioId,
        user_id: params.userId,
        active_lens_id: orderedLenses.find((lens) => lens.is_active_lens)?.lens_id ?? null,
        lenses: orderedLenses.map((lens) => ({
            lens_id: lens.lens_id,
            is_selected: lens.is_selected,
            display_order: lens.display_order,
        })),
        column_visibility,
    };
};
